import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import TabBar from '../../components/TabBar';
import { useChat } from '../../contexts/ChatContext';
import { v2ListSupportClients } from '../../services/v2/api';
import { COLORS } from '../../theme/themeTokens';
import { V2SupportClientFilter, V2SupportClientSummary } from '../../types/v2';
import SupportHeader from './SupportHeader';

const PAGE_SIZE = 50;
const FILTER_LABELS: Record<V2SupportClientFilter, string> = {
  all: 'All clients',
  unread: 'Unread',
  with_chats: 'With chats',
};

const FilterChevron = () => (
  <Svg width={11} height={6} viewBox="0 0 11 6" fill="none">
    <Path
      d="M9.98799 0.144249C10.0337 0.0985194 10.088 0.0622454 10.1478 0.0374971C10.2075 0.0127487 10.2715 1.00414e-05 10.3362 1.00386e-05C10.4009 1.00358e-05 10.4649 0.0127487 10.5247 0.0374971C10.5844 0.0622454 10.6387 0.0985193 10.6844 0.144248C10.7302 0.189978 10.7664 0.244267 10.7912 0.304015C10.8159 0.363763 10.8287 0.427801 10.8287 0.492472C10.8287 0.557142 10.8159 0.62118 10.7912 0.680928C10.7664 0.740676 10.7302 0.794965 10.6844 0.840694L5.76256 5.76257C5.71685 5.80833 5.66256 5.84463 5.60281 5.8694C5.54306 5.89417 5.47902 5.90692 5.41434 5.90692C5.34965 5.90692 5.28561 5.89417 5.22586 5.8694C5.16611 5.84463 5.11182 5.80833 5.06611 5.76257L0.144238 0.840695C0.0518837 0.748341 -4.64503e-07 0.623081 -4.73346e-07 0.492472C-4.8219e-07 0.361863 0.0518836 0.236604 0.144238 0.144249C0.236592 0.0518947 0.361852 1.04746e-05 0.492461 1.04689e-05C0.623069 1.04632e-05 0.748329 0.0518946 0.840683 0.144249L5.41434 4.71852L9.98799 0.144249Z"
      fill={COLORS.white}
    />
  </Svg>
);

interface Props {
  onBack: () => void;
  onClient: (client: V2SupportClientSummary) => void;
  onNavigate: (screen: string) => void;
}

const SupportClientListScreen: React.FC<Props> = ({ onBack, onClient, onNavigate }) => {
  const [clients, setClients] = useState<V2SupportClientSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<V2SupportClientFilter>('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestID = useRef(0);
  const { refreshClientSupportUnreadCount } = useChat();

  const load = useCallback(async (offset: number, mode: 'replace' | 'append' | 'poll') => {
    const id = ++requestID.current;
    if (mode === 'replace') setLoading(true);
    if (mode === 'append') setLoadingMore(true);
    try {
      const response = await v2ListSupportClients({
        search: search.trim() || undefined,
        filter,
        limit: PAGE_SIZE,
        offset,
      });
      if (id !== requestID.current) return;
      setTotal(response.total);
      setError(null);
      setClients(previous => {
        if (mode === 'replace') return response.clients || [];
        if (mode === 'append') {
          const known = new Set(previous.map(item => item.user_id));
          return [...previous, ...(response.clients || []).filter(item => !known.has(item.user_id))];
        }
        const firstPageIds = new Set((response.clients || []).map(item => item.user_id));
        return [...(response.clients || []), ...previous.filter(item => !firstPageIds.has(item.user_id))]
          .slice(0, Math.max(previous.length, response.clients?.length || 0));
      });
      void refreshClientSupportUnreadCount();
    } catch (err) {
      if (id === requestID.current) {
        setError(err instanceof Error ? err.message : 'Unable to load clients');
      }
    } finally {
      if (id === requestID.current) {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
      }
    }
  }, [filter, refreshClientSupportUnreadCount, search]);

  useEffect(() => {
    const debounce = setTimeout(() => { void load(0, 'replace'); }, 300);
    return () => clearTimeout(debounce);
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => { void load(0, 'poll'); }, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const onRefresh = () => {
    setRefreshing(true);
    void load(0, 'replace');
  };

  const loadMore = () => {
    if (!loading && !loadingMore && clients.length < total) {
      void load(clients.length, 'append');
    }
  };

  const renderClient = ({ item }: { item: V2SupportClientSummary }) => (
    <TouchableOpacity style={styles.clientRow} onPress={() => onClient(item)} activeOpacity={0.7}>
      <View style={styles.avatar}>
        <Image source={require('../../assets/dashboard-header-person.png')} style={styles.personIcon} />
      </View>
      <View style={styles.clientText}>
        <View style={styles.loginRow}>
          <Text style={styles.login} numberOfLines={1}>{item.user_login}</Text>
          {item.unread_count > 0 && <View style={styles.unreadDot} />}
        </View>
        {!!item.last_message_text && (
          <Text style={styles.preview} numberOfLines={1}>{item.last_message_text}</Text>
        )}
      </View>
      {item.unread_count > 0 && (
        <View style={styles.countBadge}>
          <Text style={styles.countText}>{item.unread_count > 99 ? '99+' : item.unread_count}</Text>
        </View>
      )}
      <Image source={require('../../assets/arrow.png')} style={styles.arrow} />
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <SupportHeader onBack={onBack} />
      <View style={styles.toolbar}>
        <View style={styles.searchBox}>
          <View style={styles.searchIcon}><View style={styles.searchHandle} /></View>
          <TextInput
            value={search}
            onChangeText={text => setSearch(text.slice(0, 50))}
            placeholder="Search by Login"
            placeholderTextColor={COLORS.blueSteel}
            autoCapitalize="none"
            autoCorrect={false}
            style={styles.searchInput}
          />
        </View>
        <TouchableOpacity style={styles.filterButton} onPress={() => setFilterOpen(true)}>
          <Text style={styles.filterText}>{FILTER_LABELS[filter]}</Text>
          <View style={styles.chevron}><FilterChevron /></View>
        </TouchableOpacity>
      </View>

      {loading && clients.length === 0 ? (
        <View style={styles.center}><ActivityIndicator color={COLORS.primaryNavy} /></View>
      ) : error && clients.length === 0 ? (
        <View style={styles.center}>
          <Text style={styles.error}>{error}</Text>
          <TouchableOpacity onPress={() => void load(0, 'replace')}><Text style={styles.retry}>Try again</Text></TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={clients}
          keyExtractor={item => item.user_id}
          renderItem={renderClient}
          contentContainerStyle={[styles.list, clients.length === 0 && styles.emptyList]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          onEndReached={loadMore}
          onEndReachedThreshold={0.35}
          ListEmptyComponent={<Text style={styles.empty}>No clients found</Text>}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={styles.more} color={COLORS.primaryNavy} /> : null}
        />
      )}

      <TabBar activeTab="support" onNavigate={onNavigate} />

      <Modal visible={filterOpen} transparent animationType="fade" onRequestClose={() => setFilterOpen(false)}>
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={() => setFilterOpen(false)}>
          <View style={styles.filterMenu}>
            {(Object.keys(FILTER_LABELS) as V2SupportClientFilter[]).map(value => (
              <TouchableOpacity
                key={value}
                style={[styles.filterOption, value === filter && styles.filterOptionActive]}
                onPress={() => { setFilter(value); setFilterOpen(false); }}>
                <Text style={[styles.optionText, value === filter && styles.optionTextActive]}>{FILTER_LABELS[value]}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.pureLight },
  toolbar: { flexDirection: 'row', padding: 12, gap: 8 },
  searchBox: { flex: 1, height: 40, flexDirection: 'row', alignItems: 'center', borderRadius: 8, backgroundColor: COLORS.softGrey, paddingHorizontal: 12 },
  searchIcon: { width: 14, height: 14, borderWidth: 1.5, borderColor: COLORS.blueSteel, borderRadius: 7, marginRight: 9, transform: [{ translateY: -1 }] },
  searchHandle: { position: 'absolute', width: 6, height: 1.5, backgroundColor: COLORS.blueSteel, right: -5, bottom: -2, transform: [{ rotate: '45deg' }] },
  searchInput: { flex: 1, padding: 0, color: COLORS.primaryNavy, fontSize: 14 },
  filterButton: { minWidth: 92, height: 40, borderRadius: 8, backgroundColor: COLORS.primaryNavy, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10 },
  filterText: { color: COLORS.white, fontSize: 12 },
  chevron: { marginLeft: 7 },
  list: { paddingHorizontal: 12, paddingBottom: 90 },
  emptyList: { flexGrow: 1, justifyContent: 'center' },
  clientRow: { minHeight: 70, flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.white, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: COLORS.softGrey },
  avatar: { width: 40, height: 40, backgroundColor: COLORS.pureLight, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  personIcon: { width: 24, height: 24, tintColor: COLORS.primaryNavy },
  clientText: { flex: 1, marginLeft: 10 },
  loginRow: { flexDirection: 'row', alignItems: 'center' },
  login: { color: COLORS.primaryNavy, fontSize: 14, maxWidth: '92%' },
  unreadDot: { width: 6, height: 6, backgroundColor: COLORS.brandOrange, borderRadius: 3, marginLeft: 4 },
  preview: { color: COLORS.blueSteel, fontSize: 12, marginTop: 4 },
  countBadge: { minWidth: 20, height: 20, borderRadius: 10, backgroundColor: COLORS.brandOrange, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5 },
  countText: { color: COLORS.white, fontSize: 10, fontWeight: '600' },
  arrow: { width: 13, height: 13, marginLeft: 8, tintColor: COLORS.blueSteel, transform: [{ rotate: '180deg' }] },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingBottom: 80, paddingHorizontal: 24 },
  error: { color: COLORS.brandOrange, textAlign: 'center', marginBottom: 12 },
  retry: { color: COLORS.primaryNavy, fontWeight: '600' },
  empty: { color: COLORS.blueSteel, textAlign: 'center' },
  more: { marginVertical: 16 },
  overlay: { flex: 1, backgroundColor: COLORS.overlay, justifyContent: 'center', padding: 28 },
  filterMenu: { backgroundColor: COLORS.white, borderRadius: 12, padding: 8 },
  filterOption: { paddingVertical: 14, paddingHorizontal: 16, borderRadius: 8 },
  filterOptionActive: { backgroundColor: COLORS.pureLight },
  optionText: { color: COLORS.primaryNavy, fontSize: 15 },
  optionTextActive: { fontWeight: '600' },
});

export default SupportClientListScreen;
